/**
 * PowerupCostCard — Stardust, Candy and Candy XL between two levels (plan phase 4, decision 0020).
 *
 * The backend's ``powerup_cost_card`` carries the asked range's totals, the
 * Shadow / purified / lucky modifier when one applied, and the cumulative
 * milestones from level 1 (the expanded answer). Rendered by CardRenderer.
 */
import type { VisualPayload } from "../../types";
import CardShell from "./_CardShell";

interface Props { payload: VisualPayload }
interface Milestone { to_level: number; stardust: number; candy: number; xl_candy: number }

const MODIFIER_LABEL: Record<string, string> = { shadow: "Shadow (+20%)", purified: "Purified (−10%)", lucky: "Lucky (half Stardust)" };

export default function PowerupCostCard({ payload }: Props) {
  const sec = payload.sections.find((s) => s.section === "powerup_cost") as Record<string, unknown> | undefined;
  if (!sec) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
        <p className="text-sm text-white/40 py-4 text-center">No power-up cost table on file.</p>
      </CardShell>
    );
  }
  const milestones = Array.isArray(sec.milestones) ? (sec.milestones as Milestone[]) : [];
  const modifier = typeof sec.modifier === "string" ? MODIFIER_LABEL[sec.modifier] ?? sec.modifier : null;
  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      <div className="space-y-3">
        <div className="rounded-xl border border-white/[0.07] p-3">
          <p className="text-[10px] text-white/30 uppercase tracking-wide">Level {String(sec.from_level)} → {String(sec.to_level)}{modifier ? ` · ${modifier}` : ""}</p>
          <div className="flex items-center gap-3 mt-2">
            <Stat label="Stardust" value={sec.stardust as number} />
            <Stat label="Candy" value={sec.candy as number} />
            <Stat label="Candy XL" value={sec.xl_candy as number} />
          </div>
        </div>
        {milestones.length > 0 && (
          <div>
            <p className="text-[10px] text-white/30 uppercase tracking-wide mb-1.5">From level 1 to…</p>
            <div className="space-y-1">
              {milestones.map((m) => (
                <div key={m.to_level} className="flex items-center justify-between text-xs">
                  <span className="text-white/60">Level {m.to_level}</span>
                  <span className="font-mono text-white/80">
                    {m.stardust.toLocaleString()} dust · {m.candy.toLocaleString()} candy{m.xl_candy ? ` · ${m.xl_candy.toLocaleString()} XL` : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </CardShell>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="text-center flex-1">
      <p className="text-[11px] text-white/30 uppercase tracking-wide">{label}</p>
      <p className="text-sm font-bold text-white">{(value ?? 0).toLocaleString()}</p>
    </div>
  );
}
